<script setup lang="ts">
import { computed, ref } from 'vue'
import { CloseBold, ArrowLeftBold, ArrowRight } from '@element-plus/icons-vue'
import ChipSelectGroup from '@/components/SearchResult/ChipSelectGroup.vue'
import FilterGroup from '@/components/MenuNav/FilterGroup.vue'
import { useSearchQueryStore } from '@/stores/searchQuery.js'
import { useFilterStore } from '@/stores/filter.js'
import { useCategoryStore } from '@/stores/category.js'
import { useSearchResultStore } from '@/stores/searchResult'
import { useSearchWithStore } from '@/composables/useSearchWithStore'

const searchQueryStore = useSearchQueryStore()
const filterStore = useFilterStore()
const categoryStore = useCategoryStore()
const searchResultStore = useSearchResultStore()
const { handleSearchWithStore } = useSearchWithStore()

const usageCategoryId = import.meta.env.VITE_APP_BASE_USAGE_CATEGORY_ID

const isShow = ref(false)
const view = ref<'main' | 'more'>('main')
const showSpaceType = ref(false)

function show(): void {
  view.value = 'main'
  isShow.value = true
}
function hide(): void {
  isShow.value = false
}
defineExpose({ show, hide })

const total = computed(() => searchResultStore.getSpaceListTotal)
const usageList = computed(() => categoryStore.getUsageCategories)
const equipmentList = computed(() => filterStore.getEquipmentList)
const serviceList = computed(() => filterStore.getServiceList)

// 空間類型(用途,單選)
const selectedUsageId = computed<number>(() => searchQueryStore.getSearchCategoryIds?.[usageCategoryId]?.[0] ?? 0)
function onUsageChange(id: number): void {
  if (id === 0) {
    searchQueryStore.clearCategoryObjectByKey(usageCategoryId)
  } else {
    searchQueryStore.setSelectedCategoryObject({ [usageCategoryId]: [id] })
  }
}

// 設備/特色(多選,切換 filterStore.selected,套用時才寫入查詢)
function toggleEquipment(item: { selected?: boolean }): void {
  item.selected = !item.selected
}
function toggleService(item: { selected?: boolean }): void {
  item.selected = !item.selected
}

function applyAndSearch(): void {
  searchQueryStore.setEquipmentIds(filterStore.getSelectedEquipment)
  searchQueryStore.setServiceIds(filterStore.getSelectedService)
  searchQueryStore.initGeoSearch()
  searchQueryStore.setPagination({ page: 1 })
  handleSearchWithStore()
  view.value = 'main'
  hide()
}

function clearAll(): void {
  // 人數/價格已移到搜尋列,此處只清篩選條件擁有的:空間類型 + 設備/服務
  searchQueryStore.clearCategoryObjectByKey(usageCategoryId)
  filterStore.clearFilterSelected()
}
</script>

<template>
  <Transition name="sheet-fade">
    <div v-show="isShow"
         class="sheet-mask"
         @click.self="hide">
      <div class="sheet">
        <!-- 主畫面 -->
        <template v-if="view === 'main'">
          <header class="sheet-header">
            <h3>{{ $t('filter.title') }}</h3>
            <el-icon :size="22"
                     class="cursor-pointer"
                     @click="hide">
              <CloseBold />
            </el-icon>
          </header>

          <div class="sheet-body">
            <!-- 人數/價格已移到搜尋列(SearchBar 四欄),此處不再重複 -->
            <!-- 空間類型 -->
            <div class="sheet-row sheet-row--first"
                 @click="showSpaceType = !showSpaceType">
              <span>{{ $t('filter.space_type_title') }}</span>
              <el-icon :size="20">
                <ArrowRight />
              </el-icon>
            </div>
            <ChipSelectGroup v-show="showSpaceType"
                             title=""
                             :options="usageList"
                             :model-value="selectedUsageId"
                             @update:model-value="onUsageChange" />

            <!-- 更多條件 -->
            <div class="sheet-row"
                 @click="view = 'more'">
              <div>
                <div>{{ $t('filter.more_conditions') }}</div>
                <div class="sheet-row-hint">{{ $t('filter.more_conditions_hint') }}</div>
              </div>
              <el-icon :size="20">
                <ArrowRight />
              </el-icon>
            </div>
          </div>

          <footer class="sheet-footer">
            <button type="button"
                    class="sheet-btn-primary"
                    @click="applyAndSearch">
              {{ $t('filter.view_results') }}（{{ $t('result.total', { t: total }) }}）
            </button>
            <button type="button"
                    class="sheet-btn-text"
                    @click="clearAll">
              {{ $t('filter.clear_all') }}
            </button>
          </footer>
        </template>

        <!-- 更多條件 -->
        <template v-else>
          <header class="sheet-header">
            <el-icon :size="22"
                     class="cursor-pointer"
                     @click="view = 'main'">
              <ArrowLeftBold />
            </el-icon>
            <h3>{{ $t('filter.more_conditions') }}</h3>
            <el-icon :size="22"
                     class="cursor-pointer"
                     @click="hide">
              <CloseBold />
            </el-icon>
          </header>

          <div class="sheet-body">
            <FilterGroup v-if="equipmentList?.length"
                         :title="$t('filter.equipment_title')"
                         :list="equipmentList"
                         :handle-click="toggleEquipment" />
            <FilterGroup v-if="serviceList?.length"
                         class="mt-[28px]"
                         :title="$t('filter.service_title')"
                         :list="serviceList"
                         :handle-click="toggleService" />
          </div>

          <footer class="sheet-footer">
            <button type="button"
                    class="sheet-btn-primary"
                    @click="applyAndSearch">
              {{ $t('filter.apply') }}
            </button>
            <button type="button"
                    class="sheet-btn-text"
                    @click="clearAll">
              {{ $t('filter.clear_all') }}
            </button>
          </footer>
        </template>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.sheet-mask {
  @apply fixed inset-0 z-[60] flex items-end bg-black/40;

  .sheet {
    @apply w-full max-h-[88vh] flex flex-col bg-osn-white-00 rounded-t-[20px];

    .sheet-header {
      @apply flex items-center justify-between px-[20px] py-[16px] border-b border-osn-grey-03;

      h3 {
        @apply flex-1 text-center text-[18px] font-semibold text-osn-dark-grey-00;
      }
    }

    .sheet-body {
      @apply flex-1 overflow-y-auto px-[20px] py-[20px];

      .sheet-row {
        @apply mt-[24px] py-[14px] flex items-center justify-between border-t border-osn-grey-03
        text-[16px] font-semibold text-osn-dark-grey-00 cursor-pointer;

        &.sheet-row--first {
          @apply mt-0 border-t-0;
        }

        .sheet-row-hint {
          @apply mt-[4px] text-[13px] font-normal text-osn-grey-01;
        }
      }
    }

    .sheet-footer {
      @apply px-[20px] py-[16px] border-t border-osn-grey-03;

      .sheet-btn-primary {
        @apply w-full h-[48px] text-[16px] font-semibold text-osn-dark-grey-00
        bg-osn-yellow-00 rounded-[12px] cursor-pointer;
      }

      .sheet-btn-text {
        @apply w-full mt-[10px] h-[40px] text-[15px] text-osn-grey-01 cursor-pointer;
      }
    }
  }
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;

  .sheet {
    transition: transform 0.25s ease;
  }
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;

  .sheet {
    transform: translateY(100%);
  }
}
</style>
